import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { applyThemePreference, readStoredPreference, type ThemePreference } from '../theme/theme';
import styles from './theme-toggle.component.module.css';

const ORDER: ThemePreference[] = ['light', 'dark'];
const GLYPH: Record<ThemePreference, string> = { light: '☀', dark: '☾' };

interface ThemeToggleProps {
	/** Server-stored preference (cross-device); wins over the local cookie when set. */
	initialPreference?: ThemePreference;
	/** Called with the new preference on each change, e.g. to persist it server-side. */
	onPreferenceChange?: (preference: ThemePreference) => void;
	/** Show just the glyph (no text label), e.g. in a collapsed sidebar rail. */
	iconOnly?: boolean;
}

export function ThemeToggle({
	initialPreference,
	onPreferenceChange,
	iconOnly,
}: ThemeToggleProps = {}) {
	const { t } = useTranslation();
	const [preference, setPreference] = useState<ThemePreference>(initialPreference ?? 'light');

	// After mount, sync to the persisted preference so SSR and first paint agree.
	// A server-provided preference (follows the user across devices) wins; otherwise
	// fall back to the locally stored one.
	useEffect(() => {
		if (initialPreference) {
			setPreference(initialPreference);
			applyThemePreference(initialPreference);
		} else {
			setPreference(readStoredPreference());
		}
	}, [initialPreference]);

	function cycle() {
		const next = ORDER[(ORDER.indexOf(preference) + 1) % ORDER.length] ?? 'light';
		setPreference(next);
		applyThemePreference(next);
		onPreferenceChange?.(next);
	}

	return (
		<button
			type="button"
			className={styles.toggle}
			data-icon-only={iconOnly || undefined}
			onClick={cycle}
			title={t('theme.toggle', 'Switch theme')}
			aria-label={iconOnly ? t('theme.toggle', 'Switch theme') : undefined}
		>
			<span aria-hidden>{GLYPH[preference]}</span>
			{!iconOnly ? <span>{t(`theme.${preference}`, preference)}</span> : null}
		</button>
	);
}
